import React, {useMemo, useState} from "react";
import {
    Box,
    Button,
    HStack,
    Heading,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Stack,
    Text
} from "@chakra-ui/react";
import {
    AREA_FLOW_SEQUENTIAL,
    AREA_FLOW_SPATIAL,
    AREA_GROUP_MODE_USER_CREATED,
    AreaFlowMode,
    AudioMode
} from "../types";

export type WizardChoices = {
    flowMode: AreaFlowMode;
    audioMode: AudioMode;
    autoPlay: boolean;
    useGroups: boolean;
};

type Props = {
    isOpen: boolean;
    canUseSequentialFlow: boolean;
    canUseStreaming: boolean;
    onSkip: () => void;
    onComplete: (choices: WizardChoices) => Promise<void> | void;
};

type StepKey = "flow" | "audio" | "trigger" | "groups";

const SpaceCreationWizard = ({isOpen, canUseSequentialFlow, canUseStreaming, onSkip, onComplete}: Props) => {
    const steps = useMemo<StepKey[]>(() => {
        const list: StepKey[] = [];
        if (canUseSequentialFlow) {
            list.push("flow");
        }
        if (canUseStreaming) {
            list.push("audio");
        }
        list.push("trigger", "groups");
        return list;
    }, [canUseSequentialFlow, canUseStreaming]);

    const [stepIndex, setStepIndex] = useState<number>(0);
    const [flowMode, setFlowMode] = useState<AreaFlowMode>(AREA_FLOW_SPATIAL);
    const [audioMode, setAudioMode] = useState<AudioMode>("download");
    const [autoPlay, setAutoPlay] = useState<boolean>(true);
    const [useGroups, setUseGroups] = useState<boolean>(false);
    const [isFinishing, setIsFinishing] = useState<boolean>(false);

    const currentStep = steps[stepIndex];
    const isFirst = stepIndex === 0;
    const isLast = stepIndex === steps.length - 1;

    const handleNext = () => {
        if (isLast) {
            void handleFinish();
            return;
        }
        setStepIndex(stepIndex + 1);
    };

    const handleBack = () => {
        if (isFirst) {
            return;
        }
        setStepIndex(stepIndex - 1);
    };

    const handleFinish = async () => {
        setIsFinishing(true);
        try {
            await onComplete({flowMode, audioMode, autoPlay, useGroups});
        } finally {
            setIsFinishing(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case "flow":
                return (
                    <Stack spacing={4}>
                        <Heading size={"sm"}>Space flow</Heading>
                        <Text>How is the space experienced?</Text>
                        <RadioGroup value={flowMode} onChange={(value) => setFlowMode(value as AreaFlowMode)}>
                            <Stack spacing={3}>
                                <Radio value={AREA_FLOW_SPATIAL}>
                                    <Box>
                                        <Text fontWeight={600}>Spatial (Spot list)</Text>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            Visitors walk around a physical space and spots trigger when they enter a defined range (BLE / GPS / Wi-Fi). Authored in the spot list with a map.
                                        </Text>
                                    </Box>
                                </Radio>
                                <Radio value={AREA_FLOW_SEQUENTIAL}>
                                    <Box>
                                        <Text fontWeight={600}>Sequential (Cue list)</Text>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            Content plays in a defined order like an audio guide or podcast. Authored in the cue list.
                                        </Text>
                                    </Box>
                                </Radio>
                            </Stack>
                        </RadioGroup>
                        <Text fontSize={"sm"} color={"gray.500"}>
                            This locks in which editor you&apos;ll use. Switching later changes the authoring view, and rules created in one mode may not all apply in the other. Change later in <b>Space settings → General → Space flow</b>.
                        </Text>
                    </Stack>
                );
            case "audio":
                return (
                    <Stack spacing={4}>
                        <Heading size={"sm"}>Audio delivery</Heading>
                        <Text>How do visitors get the audio?</Text>
                        <RadioGroup value={audioMode} onChange={(value) => setAudioMode(value as AudioMode)}>
                            <Stack spacing={3}>
                                <Radio value={"download"}>
                                    <Box>
                                        <Text fontWeight={600}>Download</Text>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            The app downloads all audio up front when entering the space. Works offline after that, but uses storage on the device.
                                        </Text>
                                    </Box>
                                </Radio>
                                <Radio value={"stream"}>
                                    <Box>
                                        <Text fontWeight={600}>Stream</Text>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            The app fetches audio on demand. Smaller footprint, no upfront wait, but needs a network connection during playback.
                                        </Text>
                                    </Box>
                                </Radio>
                            </Stack>
                        </RadioGroup>
                        <Text fontSize={"sm"} color={"gray.500"}>
                            Stream is recommended for large or multi-language libraries. Change later in <b>Space settings → Sound</b>.
                        </Text>
                    </Stack>
                );
            case "trigger":
                return (
                    <Stack spacing={4}>
                        <Heading size={"sm"}>Spot triggering</Heading>
                        <Text>How do spots start?</Text>
                        <RadioGroup value={autoPlay ? "auto" : "manual"} onChange={(value) => setAutoPlay(value === "auto")}>
                            <Stack spacing={3}>
                                <Radio value={"auto"}>
                                    <Box>
                                        <Text fontWeight={600}>Auto-play</Text>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            Spots start playing automatically when the visitor enters their trigger zone (GPS, beacon, Wi-Fi). Hands-free, place-based experience.
                                        </Text>
                                    </Box>
                                </Radio>
                                <Radio value={"manual"}>
                                    <Box>
                                        <Text fontWeight={600}>Tap to play</Text>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            Nothing plays until the visitor taps a spot in the list. Useful for venues where geo-triggers aren&apos;t practical.
                                        </Text>
                                    </Box>
                                </Radio>
                            </Stack>
                        </RadioGroup>
                        <Text fontSize={"sm"} color={"gray.500"}>
                            Change later in <b>Space settings → Settings → Spot triggering</b>.
                        </Text>
                    </Stack>
                );
            case "groups":
                return (
                    <Stack spacing={4}>
                        <Heading size={"sm"}>Synchronised groups</Heading>
                        <Text>Should multiple visitors be able to listen in sync as a group?</Text>
                        <RadioGroup value={useGroups ? "yes" : "no"} onChange={(value) => setUseGroups(value === "yes")}>
                            <Stack spacing={3}>
                                <Radio value={"no"}>
                                    <Box>
                                        <Text fontWeight={600}>No</Text>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            Each visitor&apos;s playback is independent.
                                        </Text>
                                    </Box>
                                </Radio>
                                <Radio value={"yes"}>
                                    <Box>
                                        <Text fontWeight={600}>Yes</Text>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            Visitors can create or join groups; everyone in a group hears the same audio in sync. Tight sync uses the USB sync dongle; without it, members play independently.
                                        </Text>
                                    </Box>
                                </Radio>
                            </Stack>
                        </RadioGroup>
                        <Text fontSize={"sm"} color={"gray.500"}>
                            Change later in <b>Space settings → Groups</b>.
                        </Text>
                    </Stack>
                );
            default:
                return null;
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onSkip} closeOnOverlayClick={false} size={"xl"}>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>
                    Space setup — step {stepIndex + 1} of {steps.length}
                </ModalHeader>
                <ModalCloseButton aria-label={"Skip setup"}/>
                <ModalBody>
                    {renderStep()}
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={3} width={"100%"}>
                        <Button variant={"ghost"} onClick={onSkip} isDisabled={isFinishing}>
                            Skip setup
                        </Button>
                        <Box flex={1}/>
                        <Button variant={"outline"} onClick={handleBack} isDisabled={isFirst || isFinishing}>
                            Back
                        </Button>
                        <Button colorScheme={"blue"} onClick={handleNext} isLoading={isFinishing}>
                            {isLast ? "Apply" : "Next"}
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SpaceCreationWizard;
